import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Slide,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  AutoAwesomeOutlined as AutoAwesomeOutlinedIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import { ShrinkIcon, ExpandIcon, ArrowBackIosIcon } from "@ippon/shared-assets";
import { useSidebarWidth } from "@ippon/shared-layouts";
import { AIChatInterface, colors } from "@ippon/shared-components";
import type { MessageType, FileAttachment } from "@ippon/shared-components";
import {
  getPageContext,
  refetchBus,
  buildRefetchKey,
} from "@ippon/shared-utils";
import { formatTheme } from "@/theme/formatTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { RootState } from "@/store";
import {
  sendChatMessage,
  confirmChatAction,
  rejectChatAction,
  addUserMessage,
  getChatMessages,
  getChatThreads,
  clearMessages,
  warmupAgent,
  consumeChatOpen,
} from "@/store/chatSlice";
import { setSidebarCollapsed } from "@/store/sidebarSlice";
import CustomThreadSidebar from "@/components/AIChatbot/CustomThreadSidebar";
import { waitForMinimumDuration } from "./minimumLoadingDuration";
import { isUnhandledChatOpenRequest } from "./pendingChatOpenGuard";
import { resolveHistoryLayout } from "./historyLayout";
import { lockBodyScroll } from "./bodyScrollLock";
import { getInitialExpandedHistoryOpen } from "./expandedHistoryState";
import { FLOATING_CHAT_MARGIN } from "./floatingChatSize";
import { useResizableFloatingChat } from "./useResizableFloatingChat";
import { isInPageChatLinkClick } from "./chatLinkNavigation";

interface Abortable {
  abort: (reason?: string) => void;
}

export type ChatDisplayMode = "docked" | "floating" | "expanded";

const DockedPaneIcon = () => (
  <svg width="20" height="16" viewBox="0 0 24 16" fill="none">
    <rect
      x="1"
      y="1"
      width="22"
      height="14"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <rect x="15" y="3.5" width="5.5" height="9" rx="1" fill="currentColor" />
  </svg>
);

const DisplayModeIcon: React.FC<{ mode: ChatDisplayMode }> = ({ mode }) => {
  if (mode === "docked") return <DockedPaneIcon />;
  if (mode === "floating") return <ShrinkIcon />;
  return <ExpandIcon />;
};

/**
 * 左サイドバーの鏡像として右側に常設するチャットペイン。
 * DashboardLayout の rightPane スロットに差し込んで使う。
 * 会話・履歴・新規チャットを内包し、Home のランチャー送信(pendingChatOpen)も受ける。
 */
interface ChatRightPaneProps {
  /** ペイン自体を閉じる（右ペインを非表示にする）。 */
  onClose?: () => void;
  /** 最大画面状態を親レイアウトへ通知する。 */
  onExpandedChange?: (expanded: boolean) => void;
  /** 表示モードを親レイアウトへ通知する。 */
  onModeChange?: (mode: ChatDisplayMode) => void;
  /** 通常右ペインの左端ドラッグ開始。 */
  onResizeMouseDown?: React.MouseEventHandler<HTMLDivElement>;
  /** 通常表示時の右ペイン幅。最大画面との四辺補間に使う。 */
  paneWidth: number;
  /** Homeなど、外部ランチャーから開く時の起点（ビューポート座標）。 */
  openOrigin?: { x: number; y: number };
  /** Home送信時は通常ペインを経由せず、初回描画から最大表示する。 */
  initiallyExpanded?: boolean;
  /** 通常の右下ランチャーから開く時の初期表示。 */
  initialMode?: Exclude<ChatDisplayMode, "expanded">;
}

export const ChatRightPane: React.FC<ChatRightPaneProps> = ({
  onClose,
  onExpandedChange,
  onModeChange,
  onResizeMouseDown,
  paneWidth,
  openOrigin,
  initiallyExpanded = false,
  initialMode = "floating",
}) => {
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const sendPromiseRef = React.useRef<Abortable | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string>();
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [isLoadingMoreThreads, setIsLoadingMoreThreads] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(() =>
    initiallyExpanded ? getInitialExpandedHistoryOpen(isBelowMd) : false,
  );
  const [displayMode, setDisplayMode] = useState<ChatDisplayMode>(
    initiallyExpanded ? "expanded" : initialMode,
  );
  const isExpanded = displayMode === "expanded";
  const isFloating = displayMode === "floating";
  const [isClosing, setIsClosing] = useState(false);
  const [isModeTransitioning, setIsModeTransitioning] = useState(false);
  const [modeMenuAnchor, setModeMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const closeTimerRef = useRef<number>();
  const modeTransitionTimerRef = useRef<number>();
  const historyLayout = resolveHistoryLayout(isExpanded);
  const sidebarWidth = useSidebarWidth();
  const { size: floatingSize, startResize: startFloatingResize } =
    useResizableFloatingChat();
  // pendingChatOpen 消費後も開き始めの起点を変えない。
  const [openTransformOrigin] = useState(() =>
    openOrigin
      ? `${Math.max(0, Math.min(100, (openOrigin.x / window.innerWidth) * 100))}% ${Math.max(0, Math.min(100, (openOrigin.y / window.innerHeight) * 100))}%`
      : "bottom right",
  );

  useEffect(
    () => () => {
      if (closeTimerRef.current !== undefined) {
        window.clearTimeout(closeTimerRef.current);
      }
      if (modeTransitionTimerRef.current !== undefined) {
        window.clearTimeout(modeTransitionTimerRef.current);
      }
    },
    [],
  );

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(() => {
      onExpandedChange?.(false);
      onClose?.();
    }, 200);
  }, [isClosing, onClose, onExpandedChange]);

  const {
    messages: chatMessages,
    isSending,
    hasMore,
    isLoading,
    nextToken,
    threads,
    isLoadingThreads,
    threadsHasMore,
    threadsNextToken,
    messagesError,
    pendingChatOpen,
  } = useAppSelector((state: RootState) => state.chat);
  const handledPendingChatOpenRef = React.useRef<typeof pendingChatOpen>(null);

  const setChatDisplayMode = useCallback(
    (mode: ChatDisplayMode) => {
      if (mode === displayMode) return;
      if (modeTransitionTimerRef.current !== undefined) {
        window.clearTimeout(modeTransitionTimerRef.current);
      }
      setIsModeTransitioning(true);
      setDisplayMode(mode);
      onModeChange?.(mode);
      if (mode === "expanded") {
        setIsHistoryOpen(getInitialExpandedHistoryOpen(isBelowMd));
      }
      modeTransitionTimerRef.current = window.setTimeout(() => {
        setIsModeTransitioning(false);
      }, 260);
    },
    [displayMode, isBelowMd, onModeChange],
  );

  // 初期化: ウォームアップ + スレッド一覧取得。
  useEffect(() => {
    void dispatch(warmupAgent());
    dispatch(getChatThreads({ limit: 15 }));
  }, [dispatch]);

  useEffect(() => {
    if (!isLoading && isLoadingMoreMessages) setIsLoadingMoreMessages(false);
  }, [isLoading, isLoadingMoreMessages]);

  // 全画面表示中は左サイドバーを縮小し、解除・アンマウント時に元の状態へ戻す。
  useEffect(() => {
    if (!isExpanded) return;

    dispatch(setSidebarCollapsed(true));

    return () => {
      dispatch(setSidebarCollapsed(false));
    };
  }, [isExpanded, dispatch]);

  // 最大画面表示中は、背面ページのスクロールを止める。
  useEffect(() => {
    if (!isExpanded) return;
    return lockBodyScroll(document.body);
  }, [isExpanded]);

  useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded, onExpandedChange]);

  useEffect(() => {
    onModeChange?.(displayMode);
  }, [displayMode, onModeChange]);

  // 最大画面中は900px境界に追従し、広い時は履歴を表示、狭い時は閉じる。
  useEffect(() => {
    if (isExpanded) {
      setIsHistoryOpen(getInitialExpandedHistoryOpen(isBelowMd));
    }
  }, [isExpanded, isBelowMd]);

  const handleSendMessage = useCallback(
    async (
      content: string,
      files: FileAttachment[],
      threadIdOverride?: string | null,
    ) => {
      if (!content.trim() && files.length === 0) return;

      const targetThreadId =
        threadIdOverride === null
          ? undefined
          : (threadIdOverride ?? selectedThreadId);

      dispatch(
        addUserMessage({
          content,
          files: files.map((f) => ({
            id: f.id,
            name: f.name,
            type: f.type,
            url: f.url,
          })),
        }),
      );

      const fileObjects = files
        .map((f) => f.file)
        .filter((f): f is File => f !== undefined);
      const wasNewThread = !targetThreadId;
      const pageContext = getPageContext();

      const promise = dispatch(
        sendChatMessage({
          query: content,
          thread_id: targetThreadId,
          files: fileObjects.length > 0 ? fileObjects : undefined,
          pageContext,
        }),
      );
      sendPromiseRef.current = promise;

      try {
        const result = await promise;
        if (result.type === "chat/sendMessage/fulfilled") {
          if (pageContext && pageContext.entities.length > 0) {
            for (const e of pageContext.entities) {
              if (e.entity && e.id) {
                refetchBus.emit(buildRefetchKey(e.entity, e.id));
              }
            }
          }
          const payload = result.payload as {
            response: {
              thread_id?: string;
              pending_action?: { action_id: string };
            };
          };
          if (payload.response.thread_id) {
            const newThreadId = payload.response.thread_id;
            setSelectedThreadId(newThreadId);
            if (wasNewThread) {
              await new Promise((resolve) => setTimeout(resolve, 800));
              dispatch(getChatThreads({ limit: 15 }));
              if (!payload.response.pending_action) {
                dispatch(
                  getChatMessages({ thread_id: newThreadId, limit: 20 }),
                );
              }
            }
          }
        }
      } catch {
        // キャンセル/エラー時は何もしない
      }
    },
    [dispatch, selectedThreadId],
  );

  // Home のランチャー等からの要求を消費（この画面で会話を開く/送る）。
  useEffect(() => {
    if (
      !isUnhandledChatOpenRequest(
        handledPendingChatOpenRef.current,
        pendingChatOpen,
      )
    ) {
      return;
    }
    handledPendingChatOpenRef.current = pendingChatOpen;

    const { threadId, sendQuery, expand } = pendingChatOpen;
    if (expand === true) setChatDisplayMode("expanded");

    if (threadId) {
      setSelectedThreadId(threadId);
      dispatch(getChatMessages({ thread_id: threadId, limit: 20 }));
    } else {
      dispatch(clearMessages());
      setSelectedThreadId(undefined);
    }

    if (sendQuery?.trim()) {
      void handleSendMessage(sendQuery.trim(), [], threadId ?? null);
    }

    dispatch(consumeChatOpen());
  }, [pendingChatOpen, dispatch, handleSendMessage, setChatDisplayMode]);

  const handleCancelSend = useCallback(() => {
    const p = sendPromiseRef.current;
    if (p && typeof p.abort === "function") p.abort();
  }, []);

  const handleConfirmAction = useCallback(
    (messageId: string, actionId: string) => {
      dispatch(
        confirmChatAction({
          action_id: actionId,
          source_message_id: messageId,
        }),
      );
    },
    [dispatch],
  );

  const handleRejectAction = useCallback(
    (messageId: string, actionId: string) => {
      dispatch(
        rejectChatAction({ action_id: actionId, source_message_id: messageId }),
      );
    },
    [dispatch],
  );

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoading && nextToken) {
      setIsLoadingMoreMessages(true);
      dispatch(
        getChatMessages({
          limit: 15,
          next_token: nextToken,
          thread_id: selectedThreadId,
        }),
      );
    }
  }, [hasMore, isLoading, nextToken, selectedThreadId, dispatch]);

  const handleLoadMoreThreads = useCallback(async () => {
    if (
      !threadsHasMore ||
      isLoadingThreads ||
      isLoadingMoreThreads ||
      !threadsNextToken
    ) {
      return;
    }

    setIsLoadingMoreThreads(true);
    try {
      await waitForMinimumDuration(
        dispatch(getChatThreads({ limit: 15, next_token: threadsNextToken })),
      );
    } finally {
      setIsLoadingMoreThreads(false);
    }
  }, [
    threadsHasMore,
    isLoadingThreads,
    isLoadingMoreThreads,
    threadsNextToken,
    dispatch,
  ]);

  const handleSelectThread = useCallback(
    (threadId: string) => {
      setSelectedThreadId(threadId);
      dispatch(getChatMessages({ thread_id: threadId, limit: 20 }));
    },
    [dispatch],
  );

  const handleNewChat = useCallback(() => {
    dispatch(clearMessages());
    setSelectedThreadId(undefined);
    setIsHistoryOpen(false);
  }, [dispatch]);

  // 全画面中に「詳細を開く」等の画面内リンクを押したら、遷移先が見えるよう右下の小窓へ縮小する。
  // 小窓では履歴が会話の上に重なるため、押した会話が見えるよう履歴は閉じる。
  const handleConversationClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (isExpanded && isInPageChatLinkClick(event)) {
        setIsHistoryOpen(false);
        setChatDisplayMode("floating");
      }
    },
    [isExpanded, setChatDisplayMode],
  );

  const handleRetry = useCallback(() => {
    if (selectedThreadId) {
      dispatch(getChatMessages({ thread_id: selectedThreadId, limit: 20 }));
    }
  }, [selectedThreadId, dispatch]);

  const convertedMessages: MessageType[] = chatMessages.map((msg) => ({
    id: msg.message_id,
    content: msg.content,
    sender: (msg.role === "user" ? "user" : "other") as "user" | "other",
    timestamp: new Date(msg.timestamp),
    files: msg.files,
    referencedDocuments: msg.referenced_documents ?? [],
    confidence: msg.confidence,
    pendingAction: msg.pending_action
      ? {
          actionId: msg.pending_action.action_id,
          toolName: msg.pending_action.tool_name,
          status: msg.pending_action.status,
        }
      : undefined,
  }));

  return (
    <>
      {/* 全画面(拡大)時の背景オーバーレイ */}
      {isExpanded && (
        <Box
          onClick={() => setChatDisplayMode("docked")}
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: `${sidebarWidth}px`,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1300,
            animation: isClosing
              ? "chatOverlayOut 0.2s ease forwards"
              : "chatOverlayIn 0.26s ease forwards",
            "@keyframes chatOverlayIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
            "@keyframes chatOverlayOut": {
              from: { opacity: 1 },
              to: { opacity: 0 },
            },
          }}
        />
      )}
      <Box
        data-testid="chat-pane"
        data-display-mode={displayMode}
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
          overscrollBehavior: "contain",
          position: "fixed",
          // 右下の小窓は右下を固定し、ドラッグで決めた幅・高さだけ左上へ広げる。
          top: isExpanded
            ? "24px"
            : isFloating
              ? `max(${FLOATING_CHAT_MARGIN}px, calc(100vh - ${floatingSize.height + FLOATING_CHAT_MARGIN}px))`
              : 0,
          right: isExpanded || isFloating ? "24px" : 0,
          bottom: isExpanded || isFloating ? "24px" : 0,
          left: isExpanded
            ? `calc(${sidebarWidth}px + 24px)`
            : isFloating
              ? `max(${FLOATING_CHAT_MARGIN}px, calc(100vw - ${floatingSize.width + FLOATING_CHAT_MARGIN}px))`
              : `calc(100vw - ${paneWidth}px)`,
          zIndex: isExpanded ? 1301 : theme.zIndex.drawer,
          transformOrigin: openTransformOrigin,
          animation: isClosing
            ? "chatPanelClose 0.2s ease forwards"
            : "chatPanelOpen 0.26s cubic-bezier(0.16, 1, 0.3, 1)",
          transition: isModeTransitioning
            ? [
                "top 0.26s ease",
                "right 0.26s ease",
                "bottom 0.26s ease",
                "left 0.26s ease",
                "border-radius 0.26s ease",
                "box-shadow 0.26s ease",
              ].join(", ")
            : "none",
          "@keyframes chatPanelOpen": {
            from: { transform: "scale(0.5)", opacity: 0 },
            to: { transform: "scale(1)", opacity: 1 },
          },
          "@keyframes chatPanelClose": {
            from: { transform: "scale(1)", opacity: 1 },
            to: { transform: "scale(0.5)", opacity: 0 },
          },
          ...(isExpanded
            ? {
                // 拡大: 左サイドバーを避けて全画面オーバーレイ。
                borderRadius: "12px",
                boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.3)",
              }
            : isFloating
              ? {
                  borderRadius: "12px",
                  boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
                }
              : {
                  // 通常: 右ペインいっぱい。
                  borderRadius: 0,
                  boxShadow: "none",
                }),
        }}
      >
        {displayMode === "docked" && onResizeMouseDown && (
          <Box
            onMouseDown={onResizeMouseDown}
            sx={{
              position: "absolute",
              left: -3,
              top: 0,
              width: 6,
              height: "100%",
              cursor: "ew-resize",
              zIndex: 2,
            }}
          />
        )}
        {/* 右下の小窓: 左端・上端・左上角をつかんでリサイズする。 */}
        {isFloating && (
          <>
            <Box
              data-testid="chat-floating-resize-left"
              onMouseDown={startFloatingResize("left")}
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                width: 6,
                height: "100%",
                cursor: "ew-resize",
                zIndex: 2,
              }}
            />
            <Box
              data-testid="chat-floating-resize-top"
              onMouseDown={startFloatingResize("top")}
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                width: "100%",
                height: 6,
                cursor: "ns-resize",
                zIndex: 2,
              }}
            />
            <Box
              data-testid="chat-floating-resize-top-left"
              onMouseDown={startFloatingResize("top-left")}
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                width: 14,
                height: 14,
                cursor: "nwse-resize",
                zIndex: 3,
              }}
            />
          </>
        )}
        {/* ヘッダー（右下フローティングと同じ構成） */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            py: 0.5,
            borderBottom: `1px solid ${colors.border.tab}`,
            backgroundColor: "#FAFAFA",
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            <Tooltip title={isHistoryOpen ? "戻る" : "履歴"} placement="bottom">
              <IconButton
                size="small"
                onClick={() => setIsHistoryOpen(!isHistoryOpen)}
                sx={{
                  color: colors.text.primary,
                  flexShrink: 0,
                  // アイコンサイズ差で高さが変わらないよう固定。
                  width: 32,
                  height: 32,
                }}
              >
                {isHistoryOpen ? (
                  <ArrowBackIosIcon sx={{ fontSize: 16, ml: "4px" }} />
                ) : (
                  <MenuIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Tooltip>
            {/* AI アイコン（グラデーションボックス、フローティングと同じ） */}
            <Box
              sx={{
                flexShrink: 0,
                width: 20,
                height: 20,
                borderRadius: "6px",
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, #5CC8E0 100%)`,
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 1px 4px rgba(11, 157, 189, 0.25)",
              }}
            >
              <AutoAwesomeOutlinedIcon sx={{ fontSize: 13 }} />
            </Box>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: colors.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              IPPON AI エージェント
            </Typography>
          </Box>
          {/* 表示モード + 閉じる */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              flexShrink: 0,
            }}
          >
            <Tooltip title="表示モード" placement="bottom">
              <IconButton
                data-testid="chat-display-mode-button"
                aria-label="表示モード"
                aria-haspopup="menu"
                aria-expanded={Boolean(modeMenuAnchor)}
                size="small"
                onClick={(event) => setModeMenuAnchor(event.currentTarget)}
                sx={{
                  color: colors.text.primary,
                  // アイコン + 開閉トグル(▼)を並べるため横長にする。
                  width: 48,
                  height: 32,
                  gap: "2px",
                  borderRadius: "16px",
                  backgroundColor: modeMenuAnchor
                    ? "rgba(0, 0, 0, 0.06)"
                    : "transparent",
                }}
              >
                <DisplayModeIcon mode={displayMode} />
                <KeyboardArrowDownIcon
                  data-testid="chat-display-mode-toggle-icon"
                  sx={{
                    fontSize: 16,
                    transform: modeMenuAnchor ? "rotate(180deg)" : "none",
                    transition: "transform 0.2s ease",
                  }}
                />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ zIndex: 1400 }}
              anchorEl={modeMenuAnchor}
              open={Boolean(modeMenuAnchor)}
              onClose={() => setModeMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    minWidth: 220,
                    borderRadius: "10px",
                    border: `1px solid ${colors.border.tab}`,
                    boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.14)",
                  },
                },
              }}
            >
              {(
                [
                  ["docked", "画面右に固定"],
                  ["floating", "右下に表示"],
                  ["expanded", "全画面表示"],
                ] as const
              ).map(([mode, label]) => (
                <MenuItem
                  key={mode}
                  selected={displayMode === mode}
                  onClick={() => {
                    setChatDisplayMode(mode);
                    setModeMenuAnchor(null);
                  }}
                  sx={{ minHeight: 44, gap: 1 }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: "28px !important",
                      "& svg": { width: 20, height: 20 },
                    }}
                  >
                    <DisplayModeIcon mode={mode} />
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{ fontSize: 12, fontWeight: 600 }}
                  />
                  {displayMode === mode && <CheckIcon sx={{ fontSize: 20 }} />}
                </MenuItem>
              ))}
            </Menu>
            {onClose && (
              <Tooltip title="閉じる" placement="bottom">
                <IconButton
                  size="small"
                  onClick={handleClose}
                  sx={{
                    color: colors.text.primary,
                    width: 32,
                    height: 32,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* 本体（会話） + 履歴。全画面時の履歴は内部の左カラム。 */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            position: "relative",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {historyLayout === "internal" && (
            <Box
              sx={{
                width: isHistoryOpen ? 280 : 0,
                flexShrink: 0,
                minHeight: 0,
                overflow: "hidden",
                borderRight: isHistoryOpen
                  ? `1px solid ${colors.border.tab}`
                  : "none",
                transition: theme.transitions.create("width", {
                  duration: 250,
                  easing: theme.transitions.easing.easeInOut,
                }),
                "& > div > div": {
                  borderRight: "none",
                  borderBottom: "none",
                },
              }}
            >
              <Box
                sx={{
                  width: 280,
                  height: "100%",
                  transform: isHistoryOpen
                    ? "translateX(0)"
                    : "translateX(-100%)",
                  transition: theme.transitions.create("transform", {
                    duration: 250,
                    easing: theme.transitions.easing.easeInOut,
                  }),
                }}
              >
                <CustomThreadSidebar
                  threads={threads}
                  selectedThreadId={selectedThreadId}
                  onThreadSelect={handleSelectThread}
                  onNewChat={handleNewChat}
                  isLoading={isLoadingThreads || isLoadingMoreThreads}
                  hasMore={threadsHasMore}
                  onLoadMore={handleLoadMoreThreads}
                  width={280}
                  useTruncateText
                  previewMaxLength={null}
                />
              </Box>
            </Box>
          )}

          <Box
            onClick={handleConversationClick}
            sx={{ flex: 1, position: "relative", minWidth: 0 }}
          >
            <AIChatInterface
              open
              onClose={handleNewChat}
              isExpanded={isExpanded}
              primaryColor={formatTheme.colors.primary.main}
              backgroundColor="#FFFFFF"
              onSendMessage={handleSendMessage}
              onConfirmAction={handleConfirmAction}
              onRejectAction={handleRejectAction}
              onCancelSend={handleCancelSend}
              onNewChat={handleNewChat}
              messages={convertedMessages}
              isSending={isSending}
              hasMore={hasMore}
              isLoadingMore={isLoadingMoreMessages}
              onLoadMore={handleLoadMore}
              isLoadingInitial={isLoading}
              loadError={messagesError}
              onRetry={handleRetry}
            />
          </Box>

          {/* 通常右ペインの履歴は、ペイン全体へ重ねて表示する。 */}
          {historyLayout === "overlay" && isHistoryOpen && (
            <Box
              onClick={() => setIsHistoryOpen(false)}
              sx={{ position: "absolute", inset: 0, zIndex: 9 }}
            />
          )}
          {historyLayout === "overlay" && (
            <Slide
              direction="right"
              in={isHistoryOpen}
              mountOnEnter
              unmountOnExit
              timeout={250}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  "& > div": { borderRight: "none", borderBottom: "none" },
                }}
              >
                <CustomThreadSidebar
                  threads={threads}
                  selectedThreadId={selectedThreadId}
                  onThreadSelect={handleSelectThread}
                  onThreadSelectCallback={() => setIsHistoryOpen(false)}
                  onNewChat={handleNewChat}
                  isLoading={isLoadingThreads || isLoadingMoreThreads}
                  hasMore={threadsHasMore}
                  onLoadMore={handleLoadMoreThreads}
                  width={0}
                  padding="6px 16px"
                  useTruncateText
                  previewMaxLength={null}
                />
              </Box>
            </Slide>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ChatRightPane;
